<template>
  <section class="v2-container v2-section asocoins-page">
    <header class="asocoins-page__header">
      <div>
        <h1 class="v2-headline v2-headline--display-lg asocoins-member-title">
          <v-icon color="#f2a900" size="34">mdi-coins</v-icon>
          Mis Asocoins
        </h1>
        <p class="v2-body v2-body--md v2-body--muted">
          Saldo compartido por tu SOFOM. Cada 10 Asocoins equivalen a $1 MXN.
        </p>
      </div>
      <button class="asocoins-button asocoins-button--outline" type="button" :disabled="loading" @click="loadData">
        <span aria-hidden="true">↻</span>
        Actualizar
      </button>
    </header>

    <div v-if="error" class="asocoins-alert asocoins-alert--error" role="alert">{{ error }}</div>

    <div class="asocoins-grid asocoins-grid--top">
      <article class="asocoins-card asocoins-card--balance">
        <div class="asocoins-card__body">
          <div class="asocoins-balance-card__main">
            <div class="asocoins-balance-card__numbers">
              <p class="asocoins-card__label">Saldo disponible</p>
              <p class="asocoins-card__balance">{{ formatPoints(balance) }}</p>
              <p class="asocoins-card__mxn">≈ {{ formatMoney(balance / 10) }} MXN</p>
            </div>
            <img
              class="asocoins-balance-card__illustration"
              src="@/assets/images/asocoins/asocoins-falling-coins.webp"
              alt=""
              aria-hidden="true"
            />
          </div>
          <hr class="asocoins-divider" />
          <p class="v2-body v2-body--sm v2-body--muted">
            El saldo pertenece a la SOFOM y lo comparten el titular y sus submiembros.
          </p>
        </div>
      </article>

      <article class="asocoins-card">
        <div class="asocoins-card__header">
          <h2>Encuestas disponibles</h2>
        </div>
        <div class="asocoins-card__body">
          <div v-if="!surveys.length" class="asocoins-alert asocoins-alert--info">
            No hay encuestas disponibles por ahora.
          </div>
          <div v-for="survey in surveys" :key="survey._id" class="survey-row">
            <div>
              <p class="survey-row__title">{{ survey.title || survey.name || "Encuesta institucional" }}</p>
              <span class="asocoins-caption">Recompensa base: {{ formatPoints(survey.asocoinsSurvey && survey.asocoinsSurvey.baseAmount) }} Asocoins</span>
            </div>
            <button
              class="asocoins-button asocoins-button--amber"
              type="button"
              :disabled="survey.responded || selectedSurvey === survey._id"
              @click="selectSurvey(survey)"
            >
              {{ survey.responded ? "Respondida" : selectedSurvey === survey._id ? "Seleccionada" : "Responder" }}
            </button>
          </div>
          <form v-if="selectedSurvey" class="asocoins-form" @submit.prevent="submitSurvey">
            <div
              v-for="(question, questionIndex) in selectedSurveyQuestions"
              :key="questionIndex"
              class="asocoins-field asocoins-survey-question"
            >
              <p class="asocoins-field__label">{{ question.text || `Pregunta ${questionIndex + 1}` }}</p>
              <div v-if="question.options && question.options.length" class="asocoins-survey-options">
                <label v-for="(option, optionIndex) in question.options" :key="optionIndex" class="asocoins-survey-option">
                  <input
                    v-model="surveyAnswers[questionIndex]"
                    type="radio"
                    :name="`asocoins-question-${questionIndex}`"
                    :value="option"
                  />
                  <span>{{ option }}</span>
                </label>
              </div>
              <textarea
                v-else
                v-model="surveyAnswers[questionIndex]"
                class="asocoins-textarea"
                rows="2"
                :aria-label="question.text || `Respuesta a pregunta ${questionIndex + 1}`"
              ></textarea>
            </div>
            <div class="asocoins-actions">
              <button class="asocoins-button asocoins-button--primary" :disabled="submittingSurvey" type="submit">
                {{ submittingSurvey ? "Enviando…" : "Enviar respuesta" }}
              </button>
              <button class="asocoins-button asocoins-button--ghost" type="button" @click="selectedSurvey = null">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </article>
    </div>

    <article class="asocoins-card">
      <div class="asocoins-card__header">
        <h2>Estado de cuenta</h2>
        <span class="asocoins-caption">{{ movements.totalDocs || 0 }} movimientos</span>
      </div>
      <div class="asocoins-table-wrap">
        <table class="asocoins-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Origen</th>
              <th class="is-number">Importe</th>
              <th class="is-number">Saldo posterior</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="movement in movements.docs" :key="movement._id">
              <td>{{ formatDate(movement.createdAt) }}</td>
              <td>{{ movement.concept || "—" }}</td>
              <td>{{ sourceLabel(movement.source) }}</td>
              <td class="is-number" :class="movement.direction === 'DEBIT' ? 'is-debit' : 'is-credit'">
                {{ signedPoints(movement.amount, movement.direction) }}
              </td>
              <td class="is-number">{{ formatPoints(movement.balanceAfter) }}</td>
            </tr>
            <tr v-if="!movements.docs || !movements.docs.length">
              <td colspan="5" class="asocoins-empty">Aún no hay movimientos.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="movements.totalPages > 1" class="asocoins-card__footer asocoins-pagination">
        <span class="asocoins-pagination__label">Página {{ page }} de {{ movements.totalPages }}</span>
        <button class="asocoins-button asocoins-button--outline" type="button" :disabled="page <= 1" @click="page -= 1; loadMovements()">
          Anterior
        </button>
        <button class="asocoins-button asocoins-button--outline" type="button" :disabled="page >= movements.totalPages" @click="page += 1; loadMovements()">
          Siguiente
        </button>
      </div>
    </article>
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { getAsocoinsSourceLabel } from "@/common/asocoinsPresentation";

export default {
  name: "MemberDashboardV2Asocoins",
  data() {
    return {
      page: 1,
      selectedSurvey: null,
      surveyAnswers: [],
      submittingSurvey: false,
    };
  },
  computed: {
    ...mapState("asocoins", ["wallet", "movements", "surveys", "loading", "error"]),
    balance() {
      return Number(this.wallet && this.wallet.availableBalance) || 0;
    },
    selectedSurveyDocument() {
      return this.surveys.find((survey) => survey._id === this.selectedSurvey) || null;
    },
    selectedSurveyQuestions() {
      const questions = this.selectedSurveyDocument?.asocoinsSurvey?.questions;
      return Array.isArray(questions) ? questions : [];
    },
  },
  methods: {
    ...mapActions("asocoins", ["getMyWallet", "getMyMovements", "getSurveys", "respondSurvey"]),
    async loadData() {
      await Promise.all([this.getMyWallet(), this.loadMovements(), this.getSurveys()]);
    },
    loadMovements() {
      return this.getMyMovements({ page: this.page, limit: 20 });
    },
    selectSurvey(survey) {
      this.selectedSurvey = survey._id;
      const questions = survey.asocoinsSurvey && Array.isArray(survey.asocoinsSurvey.questions)
        ? survey.asocoinsSurvey.questions
        : [];
      this.surveyAnswers = questions.map(() => "");
    },
    async submitSurvey() {
      const answers = this.selectedSurveyQuestions.reduce((result, question, index) => {
        const questionId = question.id || question._id || `question-${index + 1}`;
        result[String(questionId)] = this.surveyAnswers[index] || "";
        return result;
      }, {});
      this.submittingSurvey = true;
      const result = await this.respondSurvey({ surveyId: this.selectedSurvey, answers });
      this.submittingSurvey = false;
      if (result.status) {
        this.$store.commit("notification/show", { color: "success", text: "Respuesta registrada correctamente." });
        this.selectedSurvey = null;
        this.surveyAnswers = [];
        await this.loadData();
      } else {
        this.$store.commit("notification/show", { color: "error", text: result.message });
      }
    },
    formatPoints(value) {
      return new Intl.NumberFormat("es-MX").format(Number(value) || 0);
    },
    formatMoney(value) {
      return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(value) || 0);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
    },
    sourceLabel(source) {
      return getAsocoinsSourceLabel(source);
    },
    signedPoints(value, direction) {
      return `${direction === "DEBIT" ? "−" : "+"}${this.formatPoints(value)}`;
    },
  },
  created() {
    this.loadData();
  },
};
</script>
